import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  CartExceptionEnum,
  IAddToCartExceptionPayload,
  ICartItem,
  IExposedRequestInOrder,
  INewCartItem,
  ServiceRequestOrderStatus,
} from "@gloria-public/order/domain/interfaces/cart.interface";
import {
  IExposedOrder,
  IModifyShippingDetails,
  IOrdersHistory,
  IOrdersHistoryFilters,
} from "@gloria-public/order/domain/interfaces/order.interface";
import { TranslateService } from "@ngx-translate/core";
import { AddToCartErrorComponent } from "@shared/components/add-to-cart-error/add-to-cart-error.component";
import { ApiService } from "@shared/services/api.service";
import { ConnectionEventsService } from "@shared/services/connection-events.service";
import { SessionService } from "@shared/services/session.service";
import { BehaviorSubject, Observable, catchError, combineLatest, first, map, of, switchMap, tap, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class OrderService {
  private modifiedItemIds = new Set<number>();

  private cartItemsCountSubject = new BehaviorSubject<number>(0);
  public cartItemsCount$ = this.cartItemsCountSubject.asObservable();

  private cartItemsSubject = new BehaviorSubject<ICartItem[]>([]);
  public cartItems$ = this.cartItemsSubject.asObservable();

  private readonly activeSubscriptionIdKey = "activeSubscriptionId";

  private activeSubscriptionIdSubject = new BehaviorSubject<number | null>(this.getStoredActiveSubscriptionId());
  public activeSubscriptionId$ = this.activeSubscriptionIdSubject.asObservable();

  public realCartItems$ = combineLatest([this.cartItems$, this.activeSubscriptionId$]).pipe(
    map(([items, activeSubscriptionId]) => items.filter((item) => this.isRealCartItem(item, activeSubscriptionId)))
  );
  public reserveCartItems$ = combineLatest([this.cartItems$, this.activeSubscriptionId$]).pipe(
    map(([items, activeSubscriptionId]) => items.filter((item) => !this.isRealCartItem(item, activeSubscriptionId)))
  );

  /* Calculates the amount due today in the cart */
  public dueToday$ = this.realCartItems$.pipe(map((items) => items.reduce((sum, item) => sum + item.pricePerUnit.value, 0)));

  /* Calculates recurring charge. */
  public readonly recurringCharge$ = this.realCartItems$.pipe(
    map((items) => {
      const subscription = items.find((item) => !!item.pricePerUnit.timing);

      return subscription
        ? {
            timing: subscription.pricePerUnit.timing!,
            amount: subscription.pricePerUnit.value,
          }
        : null;
    })
  );

  constructor(
    private api: ApiService,
    private sessionService: SessionService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private connectionEventsService: ConnectionEventsService
  ) {
    this.connectionEventsService.logout$.subscribe(() => {
      this.initializeCart(this.translateService.currentLang);
    });
    this.connectionEventsService.login$.subscribe(() => {
      this.initializeCart(this.translateService.currentLang);
    });
  }

  /**
   * Indicates whether authentication is required for API calls
   * @return True if user is logged in, otherwise false
   */
  private get isAuthRequired(): boolean {
    return this.sessionService.isLoggedIn;
  }

  /** Gets unique service request IDs from cart items
   * @return Set of service request template IDs
   */
  public get serviceRequestIDs(): Set<string> {
    return new Set(this.cartItemsSubject.value.map((item) => item.serviceRequestTemplateID));
  }

  /**
   * Finds order ID from service request ID
   * @param serviceRequestId Service request template ID
   * @return Matching order ID or undefined
   */
  public getOrderIdFromServiceRequestId(serviceRequestId: string): number | undefined {
    return this.cartItemsSubject.value.find((item) => item.serviceRequestTemplateID === serviceRequestId)?.id;
  }

  /**
   * Checks whether a cart item belongs to the real cart.
   * @param item Cart item to check.
   * @param activeSubscriptionId Active subscription ID.
   * @return True if the item is a real cart item.
   */
  private isRealCartItem(item: ICartItem, activeSubscriptionId: number | null): boolean {
    return !item.pricePerUnit?.timing || item.id === activeSubscriptionId;
  }

  /**
   * Updates a real cart item and marks it as modified.
   * @param itemId Cart item ID to update.
   * @param changes Changes to apply.
   * @return void.
   */
  public patchRealCartItem(itemId: number, changes: Partial<ICartItem>): void {
    const activeSubscriptionId = this.activeSubscriptionIdSubject.value;

    this.cartItemsSubject.next(
      this.cartItemsSubject.value.map((item) => {
        if (this.isRealCartItem(item, activeSubscriptionId) && item.id === itemId) {
          this.modifiedItemIds.add(itemId);
          return { ...item, ...changes };
        }

        return item;
      })
    );
  }

  /**
   * Updates matching real cart items and marks them as modified.
   * @param predicate Condition to select items.
   * @param changes Changes to apply.
   * @return void.
   */
  public patchRealCartItems(predicate: (item: ICartItem) => boolean, changes: Partial<ICartItem>, markAsModified = true): void {
    const activeSubscriptionId = this.activeSubscriptionIdSubject.value;

    this.cartItemsSubject.next(
      this.cartItemsSubject.value.map((item) => {
        if (this.isRealCartItem(item, activeSubscriptionId) && predicate(item)) {
          if (markAsModified) this.modifiedItemIds.add(item.id);
          return { ...item, ...changes };
        }

        return item;
      })
    );
  }

  /**
   * Checks whether a cart item was modified.
   * @param itemId Cart item ID.
   * @return True if the item is modified.
   */
  public isModified(itemId: number): boolean {
    return this.modifiedItemIds.has(itemId);
  }

  /**
   * Clears modified state for cart items.
   * @param itemIds Cart item IDs to clear.
   * @return void.
   */
  public clearModified(itemIds: number[]): void {
    itemIds.forEach((id) => this.modifiedItemIds.delete(id));
  }

  /**
   * Gets current cart items count
   * @return Number of items in cart
   */
  public getCartItemsCount(): number {
    return this.cartItemsCountSubject.value;
  }

  /**
   * Updates cart items state
   * @param items Updated cart items
   */
  private updateCartItems(items: ICartItem[]): void {
    this.cartItemsSubject.next(items);
  }

  /**
   * Updates cart items count state
   * @param count Updated item count
   */
  private updateCartItemsCount(count: number): void {
    this.cartItemsCountSubject.next(count);
  }

  /**
   * Updates cart items, count and selected subscription
   * @param items Updated cart items
   */
  private updateCart(items: ICartItem[]): void {
    this.updateCartItems(items);
    this.updateCartItemsCount(items.length);
    this.ensureActiveSubscription(items);
  }

  private getStoredActiveSubscriptionId(): number | null {
    const value = localStorage.getItem(this.activeSubscriptionIdKey);
    return value ? Number(value) : null;
  }

  public selectSubscription(subscriptionId: number | null): void {
    this.activeSubscriptionIdSubject.next(subscriptionId);

    if (subscriptionId === null) {
      localStorage.removeItem(this.activeSubscriptionIdKey);
    } else {
      localStorage.setItem(this.activeSubscriptionIdKey, subscriptionId.toString());
    }
  }

  private ensureActiveSubscription(items: ICartItem[]): void {
    const subscriptions = items.filter((item) => !!item.pricePerUnit?.timing);

    if (!subscriptions.length) {
      this.selectSubscription(null);
      return;
    }

    const current = this.activeSubscriptionIdSubject.value;

    const stillExists = subscriptions.some((item) => item.id === current);

    if (!stillExists) {
      this.selectSubscription(subscriptions[0].id);
    }
  }

  /**
   * Clears all items from cart
   */
  public resetCart(): void {
    this.updateCart([]);
  }

  /**
   * Initializes cart based on session or guest state
   * @param language Language of the data to receive
   */
  public initializeCart(language: string): void {
    if (this.sessionService.isLoggedIn || this.sessionService.isGuest) {
      this.getCartItems(language)
        .pipe(
          catchError(() => {
            this.resetCart();
            return of([]);
          })
        )
        .subscribe();
    } else {
      this.resetCart();
    }
  }

  /**
   * Recover all items in a client's cart
   * @param language language of the data to receive
   * @returns A list of items that user has in his cart
   */
  public getCartItems(language: string): Observable<ICartItem[]> {
    return this.api.get(this.api.getUpdatedPath("cart"), { lang: language }, this.isAuthRequired, this.api.appendGuestHeader()).pipe(
      map((response) => response as ICartItem[]),
      tap((items) => this.updateCart(items))
    );
  }

  /**
   * Add an item to a client's cart
   * @param item Item to add to user's cart
   * @param language language of the data to receive
   * @returns The current order infos
   */
  public addItemToCart(item: INewCartItem, language: string): Observable<IExposedRequestInOrder> {
    return this.api.post(this.api.getUpdatedPath("cart/items"), item, this.api.appendGuestHeader(), this.isAuthRequired).pipe(
      map((response) => response as IExposedRequestInOrder),
      // TODO GLOR-194 See if it is possible to change the return type of cart/items to avoid this call
      // const updated = [...this.cartItemsSubject.value, newItem];
      // this.updateCart(updated);
      switchMap((res) => this.getCartItems(language).pipe(map(() => res)))
    ) as Observable<IExposedRequestInOrder>;
  }

  /**
   * Remove an item from a client's cart
   * @param itemId Item to remove from user's cart
   * @returns Success or error
   */
  public removeItemFromCart(itemId: number): Observable<unknown> {
    return this.api
      .delete(
        this.api.getUpdatedPath(`cart/items/${encodeURIComponent(itemId)}`),
        undefined,
        this.isAuthRequired,
        this.api.appendGuestHeader()
      )
      .pipe(
        map((response) => response as unknown),
        tap(() => {
          const updated = this.cartItemsSubject.value.filter((i) => i.id !== itemId);
          this.updateCart(updated);
        })
      );
  }

  /**
   * Remove all items from a client's cart
   * @returns Success or error
   */
  public emptyCart(): Observable<unknown> {
    return this.api.delete(this.api.getUpdatedPath("cart/items"), undefined, this.isAuthRequired, this.api.appendGuestHeader()).pipe(
      map((response) => response as unknown),
      tap(() => {
        this.resetCart();
      })
    );
  }

  /**
   * Recover all orders that were paid by client
   * @returns A list of orders
   */
  public getOrders(): Observable<IExposedOrder[]> {
    return this.api.get("orders") as Observable<IExposedOrder[]>;
  }

  /**
   * Pass an order with the current items in the cart. If multiple subscriptions, user must select only one and the others will be automatically moved to a new cart.
   * @returns Stripe client secret
   */
  public addOrder(): Observable<string> {
    const id = this.activeSubscriptionIdSubject.value;

    return this.api.post("orders", undefined, undefined, true, id ? { subscriptionId: id } : undefined, "text" as "json");
  }

  /**
   * Modify billing and shipping information for client's cart items
   * @param updatedShippingDetails The updated shipping details
   * @returns Success or error
   */
  public modifyOrder(updatedShippingDetails: IModifyShippingDetails): Observable<unknown> {
    return this.api.patch("orders", updatedShippingDetails) as Observable<unknown>;
  }

  /**
   * Change the status of an item in order
   * @param orderId The id of the order to change
   * @param newStatus The new status to set for the order
   * @returns Success or error
   */
  public updateOrderStatus(orderId: number, newStatus: ServiceRequestOrderStatus): Observable<unknown> {
    return this.api.patch(`orders/${encodeURIComponent(orderId)}/status`, newStatus) as Observable<unknown>;
  }

  /**
   * Cancel an active service request
   * @param serviceRequestId Service request ID
   */
  public cancelSubscription(serviceRequestId: number): Observable<unknown> {
    return this.api.post(`orders/requests/${encodeURIComponent(serviceRequestId)}/cancel`, undefined) as Observable<unknown>;
  }

  /**
   * Returns a paginated list of non-draft orders for the user
   * @param filters Filters to use for pagination and sorting
   * @returns A paginated list of orders
   */
  public getOrdersHistory(filters: IOrdersHistoryFilters): Observable<IOrdersHistory[]> {
    const params: Record<string, string | number> = {
      lang: filters.lang,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 10,
    };

    if (filters.sortByDate) params["sortByDate"] = filters.sortByDate;

    return this.api.get("orders/history", params) as Observable<IOrdersHistory[]>;
  }

  /**
   * Returns the total count of non-draft orders for the user
   * @returns The total count of orders
   */
  public getOrdersHistoryCount(): Observable<number> {
    return this.api.get("orders/history/count") as Observable<number>;
  }

  /**
   * Recover the status of a stripe checkout session.
   * @returns Stripe payment status
   */
  public getSessionStatus(sessionID: string): Observable<string> {
    return this.api.get(
      `payments/${encodeURIComponent(sessionID)}/status`,
      undefined,
      true,
      undefined,
      "text" as "json"
    ) as Observable<string>;
  }

  // HIGH-LEVEL FLOW API

  public addToCartFlow(item: INewCartItem, title: string): Observable<IExposedRequestInOrder> {
    return this.api.initGuestIfRequired().pipe(
      switchMap(() => {
        return this.addItemToCart(item, this.translateService.currentLang);
      }),
      catchError((err) => {
        if (err.status === 409) {
          const code = err?.error?.error;

          if (code == CartExceptionEnum.ITEM_ALREADY_IN_CART || code == CartExceptionEnum.SERVICE_ALREADY_SUBSCRIBED) {
            // If a conflict happens it can mean the cart was updated in another tab and must be refreshed
            this.getCartItems(this.translateService.currentLang).pipe(first()).subscribe();
          }

          const required = err?.error?.required as IAddToCartExceptionPayload | undefined;

          this.dialog.open(AddToCartErrorComponent, {
            maxWidth: "100vw",
            maxHeight: "100vh",
            data: {
              errors: [
                {
                  code: code,
                  items: [
                    {
                      name: title,
                      item: item,
                    },
                  ],
                },
              ],
              required: required,
            },
          });
        } else {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        }

        return throwError(() => err);
      }),
      first()
    );
  }

  public removeFromCartFlow(serviceRequestId: string): Observable<unknown> {
    const orderId = this.getOrderIdFromServiceRequestId(serviceRequestId);

    if (!orderId) {
      return of(null);
    }

    return this.removeItemFromCart(orderId).pipe(
      catchError((err) => {
        if (err.status === 404) {
          // If it happens it means the cart was updated in another tab and must be refreshed
          this.getCartItems(this.translateService.currentLang).pipe(first()).subscribe();
        } else {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        }

        return throwError(() => err);
      }),
      first()
    );
  }
}
